/**
 * Feedback System Types
 * Enhancement requests, bug reports, and auto-error detection
 */

// =====================================================
// Core Enums
// =====================================================

export type FeedbackType = 'bug' | 'enhancement' | 'question';

export type FeedbackStatus =
  | 'submitted'
  | 'triaged'
  | 'planned'
  | 'in_progress'
  | 'resolved'
  | 'closed'
  | 'wont_fix'
  | 'duplicate';

export type FeedbackPriority = 'low' | 'medium' | 'high' | 'critical';

export type FeedbackSeverity = 'critical' | 'major' | 'minor' | 'trivial';

export type FeedbackCategory =
  | 'rate-quotes'
  | 'pre-approvals'
  | 'contacts'
  | 'documents'
  | 'pipeline'
  | 'admin'
  | 'encompass'
  | 'ai-features'
  | 'other';

export type FeedbackSource = 'manual' | 'auto_detected';

export type ErrorType = 'js_exception' | 'console_error' | 'api_failure' | 'unhandled_rejection';

// =====================================================
// Environment Capture
// =====================================================

export interface FeedbackEnvironment {
  browser?: string;
  browser_version?: string;
  os?: string;
  screen_width?: number;
  screen_height?: number;
  viewport_width?: number;
  viewport_height?: number;
  page_url?: string;
  page_title?: string;
  user_agent?: string;
  timestamp?: string;
  timezone?: string;
}

// =====================================================
// Attachment Types
// =====================================================

export interface FeedbackAttachment {
  name: string;
  path: string;
  type: string;
  size: number;
  url?: string;
}

// =====================================================
// Core Entity Types
// =====================================================

export interface FeedbackRequest {
  id: string;
  ticket_id: string;

  // Core fields
  title: string;
  type: FeedbackType;
  description: string;
  category: FeedbackCategory;

  // Bug-specific fields
  severity: FeedbackSeverity | null;
  steps_to_reproduce: string | null;
  expected_behavior: string | null;
  actual_behavior: string | null;

  // Source tracking
  source: FeedbackSource;
  error_group_id: string | null;

  // Environment
  environment: FeedbackEnvironment;

  // Status & Priority
  status: FeedbackStatus;
  priority: FeedbackPriority | null;

  // Assignments
  submitted_by: string;
  assigned_to: string | null;

  // Resolution
  resolution: string | null;
  duplicate_of: string | null;

  // Timestamps
  created_at: string;
  updated_at: string;
  resolved_at: string | null;

  // Attachments
  attachments: FeedbackAttachment[];
}

export interface FeedbackComment {
  id: string;
  feedback_id: string;
  user_id: string;
  content: string;
  is_internal: boolean;
  created_at: string;
  updated_at: string;
}

export interface FeedbackStatusHistory {
  id: string;
  feedback_id: string;
  from_status: FeedbackStatus | null;
  to_status: FeedbackStatus;
  changed_by: string;
  note: string | null;
  created_at: string;
}

export interface AutoErrorGroup {
  id: string;
  error_hash: string;
  error_message: string;
  error_type: ErrorType;
  first_seen_at: string;
  last_seen_at: string;
  occurrence_count: number;
  feedback_id: string | null;
  is_resolved: boolean;
  sample_stack_trace: string | null;
  sample_environment: FeedbackEnvironment | null;
  affected_users: string[];
}

// =====================================================
// Extended Types with Relationships
// =====================================================

export interface FeedbackRequestWithDetails extends FeedbackRequest {
  submitter?: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
  assignee?: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  } | null;
  comments_count?: number;
  error_group?: AutoErrorGroup | null;
  duplicate_request?: {
    id: string;
    ticket_id: string;
    title: string;
  } | null;
}

export interface FeedbackCommentWithUser extends FeedbackComment {
  user: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

export interface AutoErrorGroupWithTicket extends AutoErrorGroup {
  feedback_request?: {
    id: string;
    ticket_id: string;
    title: string;
    status: FeedbackStatus;
  } | null;
}

// =====================================================
// Form/Input Types
// =====================================================

export interface CreateFeedbackInput {
  title: string;
  type: FeedbackType;
  description: string;
  category: FeedbackCategory;
  severity?: FeedbackSeverity;
  steps_to_reproduce?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  environment?: FeedbackEnvironment;
  attachments?: FeedbackAttachment[];
}

export interface UpdateFeedbackInput {
  title?: string;
  description?: string;
  category?: FeedbackCategory;
  severity?: FeedbackSeverity;
  steps_to_reproduce?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  status?: FeedbackStatus;
  priority?: FeedbackPriority;
  assigned_to?: string | null;
  resolution?: string;
  duplicate_of?: string;
}

export interface CreateCommentInput {
  feedback_id: string;
  content: string;
  is_internal?: boolean;
}

// =====================================================
// Filter Types
// =====================================================

export interface FeedbackFilters {
  status?: FeedbackStatus[];
  type?: FeedbackType[];
  priority?: FeedbackPriority[];
  category?: FeedbackCategory[];
  source?: FeedbackSource[];
  assigned_to?: string | null;
  submitted_by?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
}

export interface ErrorGroupFilters {
  error_type?: ErrorType[];
  is_resolved?: boolean;
  has_ticket?: boolean;
  search?: string;
  date_from?: string;
  date_to?: string;
}

// =====================================================
// Metrics Types
// =====================================================

export interface FeedbackMetrics {
  total: number;
  by_status: Record<FeedbackStatus, number>;
  by_type: Record<FeedbackType, number>;
  by_priority: Record<FeedbackPriority, number>;
  by_source: Record<FeedbackSource, number>;
  open_bugs: number;
  open_enhancements: number;
  critical_priority: number;
  avg_resolution_time_hours: number | null;
}

export interface ErrorGroupMetrics {
  total_groups: number;
  total_occurrences: number;
  unique_users_affected: number;
  by_type: Record<ErrorType, number>;
  unresolved_count: number;
  last_24h_count: number;
}

// =====================================================
// Auto-Error Reporting Types
// =====================================================

export interface ErrorReport {
  message: string;
  type: ErrorType;
  stack?: string;
  url?: string;
  statusCode?: number;
}

export interface ReportAutoErrorPayload {
  error_hash: string;
  error_message: string;
  error_type: ErrorType;
  stack_trace?: string;
  environment: FeedbackEnvironment;
  // user_id is intentionally not part of this contract: the edge function
  // derives it from the verified JWT. Client-supplied user IDs are ignored.
  url?: string;
  status_code?: number;
}

export interface ReportAutoErrorResponse {
  action: 'created' | 'incremented';
  group_id: string;
  ticket_id?: string;
}

// =====================================================
// UI Helper Types
// =====================================================

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  submitted: 'Submitted',
  triaged: 'Triaged',
  planned: 'Planned',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
  wont_fix: "Won't Fix",
  duplicate: 'Duplicate'
};

export const FEEDBACK_STATUS_COLORS: Record<FeedbackStatus, string> = {
  submitted: 'bg-gray-100 text-gray-800',
  triaged: 'bg-blue-100 text-blue-800',
  planned: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-600',
  wont_fix: 'bg-red-100 text-red-800',
  duplicate: 'bg-orange-100 text-orange-800'
};

export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  bug: 'Bug',
  enhancement: 'Enhancement',
  question: 'Question'
};

export const FEEDBACK_TYPE_COLORS: Record<FeedbackType, string> = {
  bug: 'bg-red-100 text-red-800',
  enhancement: 'bg-blue-100 text-blue-800',
  question: 'bg-purple-100 text-purple-800'
};

export const FEEDBACK_PRIORITY_LABELS: Record<FeedbackPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical'
};

export const FEEDBACK_PRIORITY_COLORS: Record<FeedbackPriority, string> = {
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800'
};

export const FEEDBACK_SEVERITY_LABELS: Record<FeedbackSeverity, string> = {
  critical: 'Critical',
  major: 'Major',
  minor: 'Minor',
  trivial: 'Trivial'
};

export const FEEDBACK_SEVERITY_COLORS: Record<FeedbackSeverity, string> = {
  critical: 'bg-red-100 text-red-800',
  major: 'bg-orange-100 text-orange-800',
  minor: 'bg-yellow-100 text-yellow-800',
  trivial: 'bg-gray-100 text-gray-800'
};

export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  'rate-quotes': 'Artemis Quote Rate',
  'pre-approvals': 'Pre-Approvals',
  contacts: 'Contacts',
  documents: 'Documents',
  pipeline: 'Pipeline',
  admin: 'Admin',
  encompass: 'Encompass',
  'ai-features': 'AI Features',
  other: 'Other'
};

export const ERROR_TYPE_LABELS: Record<ErrorType, string> = {
  js_exception: 'JS Exception',
  console_error: 'Console Error',
  api_failure: 'API Failure',
  unhandled_rejection: 'Unhandled Rejection'
};

export const ERROR_TYPE_COLORS: Record<ErrorType, string> = {
  js_exception: 'bg-red-100 text-red-800',
  console_error: 'bg-orange-100 text-orange-800',
  api_failure: 'bg-yellow-100 text-yellow-800',
  unhandled_rejection: 'bg-purple-100 text-purple-800'
};
